import React, { useState, useEffect, useCallback, useRef } from "react";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import Environment from "./Environment";
import QRScannerBox from './components/QRscannerBox';
import PinModal from './components/PinModal';
import DriverInformation from "./components/DriverInformation";
import OrderForm from "./components/OrderForm";
import HandleCompleteOrder from "./components/HandleCompleteOrder"
import HandleArrivalOrder from "./components/HandleArrivalOrder";
import OrderMessages from "./components/OrderMessages";
import Account from './components/Account';
import DebugTokenButton from './components/DebugTokenButton';

import { jwtDecode } from 'jwt-decode';
import { 
  Container, 
  Typography, 
  Paper, 
  Divider, 
  Card,
  CardContent
} from '@mui/material';  

import './App.css';
import OrderGenerator from "./OrderGenerator";
import { SignaturesMode, Signatures } from "./components/Signatures";
import OrderOverview from "./components/OrderOverview";
import AppFeatures from "./AppFeatures";
import {Order, OrderTokenType} from "./Order";
import OrderInterpreter from './OrderInterpreter';
import ConditionalRenderer from "./ConditionalRenderer";

import { useAuth0 } from "@auth0/auth0-react";
import RecurringCallback from "./RecurringCallback";
import {APIRequest} from './APIRequest';
import { inspect } from "util";

declare global {
  interface Window { extended: any; }
}

function findValueByKey(json: any, targetKey: string): any {
  if (typeof json !== "object" || json === null) {
    return undefined;
  }

  for (const key in json) {
    if (key === targetKey) {
      return json[key];
    }

    const nestedResult = findValueByKey(json[key], targetKey);
    if (nestedResult !== undefined) {
      return nestedResult;
    }
  }

  return undefined;
}

const App: React.FC = () => {
  const [decodedToken, setdecodedToken] = useState<any>(null);
  const [driverName, setDriverName] = useState<string>("null");
  const [truckDetails, setTruckDetails] = useState<string>("null");
  const [qrText, setQrText] = useState<string>("");
  const [orderId, setOrderId] = useState<string>("");
  const [purchaseOrderId, setPurchaseOrderId] = useState<string>("null");
  const [orderDescription, setOrderDescription] = useState<string>("null");
  const [statusText, setStatusText] = useState<string>("")
  const [statusShouldFlash, setStatusShouldFlash] = useState<boolean>(true)
  const [showStatus, setShowStatus] = useState<boolean>(false)
  const [order, setOrder] = useState<Order>(OrderInterpreter.Empty());

  const [deepestLayerToken, setdeepestLayerToken] = useState<any>(null);
  const [correctPin, setCorrectPin] = useState<string>("");
  const [pin, setPin] = useState<string>("");
  const [isPinModalOpen, setIsPinModalOpen] = useState<boolean>(false);
  const [pinError, setPinError] = useState<string>("");
  const [pinAvailable, setPintAvailable] = useState<boolean>(false);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraText, setCameraText] = useState<string>("Open Camera")

  const orderPolling = useRef<RecurringCallback>(new RecurringCallback());

  const { getAccessTokenSilently } = useAuth0();

  const [confirmPickUpUrl, setConfirmPickUpUrl] = useState<string>(Environment.BuildConfirmOrderPickupEndpoint(orderId));
  const [confirmEndPickUpUrl, setConfirmEndPickUpUrl] = useState<string>(Environment.BuildConfirmEndPickupEndpoint(orderId));

  const orderUrl = Environment.GetOrderUrl()
  const createOrderUrl = Environment.GetCreateorderUrl();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0); // default: Digidrop tab

  const [backgroundColor, setBackgroundColor] = useState<string>("#fff");
  const [pageTitle, setPageTitle] = useState<string>("DigiDrop App");
  const [pathname, setPathname] = useState(window.location.pathname);
  const [isInspectorApp, setIsInspectorApp] = useState(pathname.startsWith("/inspector"));


  const provideAuth = async () => {
    return "Bearer " + await getAccessTokenSilently();
  }

  const requests = {
    orderSubmitRequest:APIRequest.At(createOrderUrl)
      .usePost()
      .setContentType("application/json"),
    orderRequest: APIRequest.At(orderUrl)
      .usePost()
      .setContentType("application/json")
      .setAuthProvider(() => provideAuth()),
    acceptOrderRequest: APIRequest.At(confirmPickUpUrl)
      .usePost()
      .setAuthProvider(() => provideAuth()),
    completeOrderRequest: APIRequest.At(confirmEndPickUpUrl)
      .usePost()
      .setAuthProvider(() => provideAuth()),
    userOrdersRequest:  APIRequest.At(Environment.GetUserOrdersUrl())
      .useGet()
      .setAuthProvider(() => provideAuth()),
    signaturesRequest: APIRequest.At(Environment.BuildGetSignaturesEndpoint("{orderId}"))
      .useGet()
      .setAuthProvider(() => provideAuth())
  }

  const tryInterpretAsOrder = (token:string) => {
    if(token === '') return;
    setOrder(OrderInterpreter.InterpretFromToken(token));
  }

  const tryUpdateStatus = () => {
    if(order.hasTopLevelTokenType(OrderTokenType.None)) {
      setStatusShouldFlash(false);
      setStatusText("");
    }
    else if(order.hasTopLevelTokenType(OrderTokenType.LoadingStartTime)) {
      setStatusShouldFlash(true);
      setStatusText("Waiting for driver to finish loading...");
    }
    else if(order.hasTopLevelTokenType(OrderTokenType.RequestEndLoadingConfirmation)) {
      setStatusShouldFlash(false);
      setStatusText("Driver has finished loading.");
    }
    else if(order.containsTokenType(OrderTokenType.LoadingEndTimeConfirmation)) {
      setStatusShouldFlash(false);
      setStatusText("Loading completed.");      
    }
    else {
      setStatusShouldFlash(false);
      setStatusText("");
    }

    setShowStatus(statusText.length > 0);
  }

  const restoreDefaults = () => {
    setQrText('');
    setdecodedToken(null);
    setOrder(OrderInterpreter.Empty());
  };

  const handleOrderClick = (utid: string) => {
    console.log("orderclick for " + utid);
    setOrderId(utid);
    setSelectedTabIndex(0); // Digidrop tab
  };

  const toggleCamera = () => {
    setIsCameraOpen((prev) => !prev);
  };

  const handleQrScan = (newText: string) => {
    setPin('');
    setQrText(newText);
    setIsPinModalOpen(true);
    setCameraText("Scan again")
    toggleCamera();
  };

  const processToken = useCallback((jwtObject: any) => {
    if (jwtObject?.tokens?.[0]?.token) {
      parseToken(jwtObject);
    }
    else {
      setdeepestLayerToken(jwtObject);
    }
  }, []);

  const parseToken = (jwtObject: any) => {
    if (jwtObject.data.driver) {
      setDriverName(jwtObject.data.driver);
      setTruckDetails(jwtObject.data.truck);
      setPintAvailable(true);
    }
    processToken(jwtDecode(jwtObject.tokens[0].token))
  }

  useEffect(() => {
    tryUpdateStatus();
  }, [order]);


  useEffect(() => {
    setIsInspectorApp(pathname.startsWith("/inspector"))
  }, [pathname]);

  useEffect(() => {
    setConfirmEndPickUpUrl(Environment.BuildConfirmEndPickupEndpoint(orderId));
    setConfirmPickUpUrl(Environment.BuildConfirmOrderPickupEndpoint(orderId));

    if(orderId.length == 0) return;
    orderPolling.current.stop();
    orderPolling.current.start(() => handleCompleteOrder(orderId), 5000);
    orderPolling.current.immediate(true);
  }, [orderId]);

  useEffect(() => {
    if (deepestLayerToken === null) return
    setPurchaseOrderId(findValueByKey(deepestLayerToken.consignments, "purchaseOrderId"))
    const quantity = findValueByKey(deepestLayerToken.consignments, "quantity")
    const packagingMaterial = findValueByKey(deepestLayerToken.consignments, "packagingMaterial")
    const description = findValueByKey(deepestLayerToken.consignments, "description")
    setOrderDescription(`${quantity} ${packagingMaterial} ${description}`)
  }, [deepestLayerToken])

  const submitPin = () => {
    if (pin === correctPin.toString()) {
      setIsPinModalOpen(false);
      setPinError("");
    } else {
      setPinError("Incorrect PIN. Please try again.");
    }
  };

  useEffect(() => {
  if (isInspectorApp) {
    setBackgroundColor("#e6f0fe");
    setPageTitle("Inspector App");
  } else {
    setBackgroundColor("#fff");
    setPageTitle("DigiDrop App");
  }
  document.title = pageTitle; // Update the browser tab title
  }, [pageTitle, isInspectorApp]);


  const handleOrderSubmit = (productName: string, driverContact: string, driverName: string) => { 
    requests.orderSubmitRequest.setBody({
      order: OrderGenerator.Create(productName, driverContact, driverName)
    });
    requests.orderSubmitRequest.send()
      .onSuccess((x:Response) => {
        console.log("Created order at " + createOrderUrl);
        restoreDefaults();
      });
  }

  const handleAcceptOrder = (bool: boolean) => {
    if (!bool) {
      setStatusText("Order has been denied")
      return;
    }

    requests.acceptOrderRequest.send()
      .onSuccess((x:any) => {
        tryInterpretAsOrder(x.jwt);
        setdecodedToken(jwtDecode(x.jwt))
        orderPolling.current.reschedule()
      })
      .onError((context:any, status:string) => console.log(status));
  }

  const handleCompleteOrderWarehouse = (bool: boolean) => {
    if (!bool) {
      setStatusText("Order completion has been denied")
      return;
    }

    requests.completeOrderRequest.send()
      .onSuccess((x:any) => {
        tryInterpretAsOrder(x.jwt);
        setdecodedToken(jwtDecode(x.jwt))
        orderPolling.current.reschedule()
    });
  }

  const handleCompleteOrder = (utid: string) => { 
    APIRequest.At(`${orderUrl}/${utid}`)
      .useGet()
      .setAuthProvider(() => provideAuth())
      .setContentType("application/json")
      .send()
      .onSuccess((x:any) => {
        console.log("polled order ", x);
        tryInterpretAsOrder(x.jwt);
        setdecodedToken(jwtDecode(x.jwt));
      })
      .onError((context, errorMessage) => {
          console.error("API error:", errorMessage);
      });
  };

  useEffect(() => {
  const updatePath = () => setPathname(window.location.pathname);

  // Patch pushState and replaceState
  const origPushState = window.history.pushState;
  const origReplaceState = window.history.replaceState;

  window.history.pushState = function (...args) {
    origPushState.apply(this, args);
    window.dispatchEvent(new Event('locationchange'));
  };
  window.history.replaceState = function (...args) {
    origReplaceState.apply(this, args);
    window.dispatchEvent(new Event('locationchange'));
  };

  window.addEventListener('popstate', updatePath);
  window.addEventListener('locationchange', updatePath);

  return () => {
    window.history.pushState = origPushState;
    window.history.replaceState = origReplaceState;
    window.removeEventListener('popstate', updatePath);
    window.removeEventListener('locationchange', updatePath);
    orderPolling.current.stop();
  };
}, []);

  useEffect(() => {
    if (qrText === "") return;
    const tempJsonFromQr = JSON.parse(qrText);
    setCorrectPin(tempJsonFromQr.pin);
    
    if (isInspectorApp) {
      APIRequest.At(`${orderUrl}/${tempJsonFromQr.id}`)
      .useGet()
      .setAuthProvider(() => provideAuth())
      .setContentType("application/json")
      .send()
      .onSuccess((x:any) => {
        console.log(tempJsonFromQr.id);
        setOrderId(tempJsonFromQr.id);
        tryInterpretAsOrder(x.jwt);
        setdecodedToken(jwtDecode(x.jwt));
      })
      .onError((context, errorMessage) => {
          console.error("API error:", errorMessage);
      });
    }
    else{
      const request = APIRequest.BaseOn(requests.orderRequest);
      request.setBody({
        id: tempJsonFromQr.id,
        origin: tempJsonFromQr.origin,
        endpoint: tempJsonFromQr.endpoint
      });
      request.send()
        .onSuccess((x:any) => {
          setOrderId(tempJsonFromQr.id);
          tryInterpretAsOrder(x.jwt);
          setdecodedToken(jwtDecode(x.jwt));        
        })
        .onError((x:string) => {
          setdecodedToken("Wrong QR")
          setIsPinModalOpen(false)        
        });
    }

  }, [qrText, orderUrl, isInspectorApp]);

  useEffect(() => {
    if (decodedToken === null) return;
    if (decodedToken !== "Wrong QR") {
      processToken(decodedToken);
    }
  }, [decodedToken, processToken]);

  const handleClose = (event: any, reason: any) => {
    if (reason !== 'backdropClick') {
      setIsPinModalOpen(false)
    }
  }

  const QRScannerBoxProps = {
    handleQrScan,
    decodedToken,
    isCameraOpen,
    cameraText,
    toggleCamera,
  };

  const orderFormProps = {
    handleOrderSubmit
  }

  const featureValidator = (features: AppFeatures[]) => {
    const location = window.location.pathname;

    if(location === '/mobile') {
      return features.includes(AppFeatures.Scan) || features.includes(AppFeatures.AcceptOrder) || features.includes(AppFeatures.DenyOrder) || features.includes(AppFeatures.CompleteOrder) || features.includes(AppFeatures.OrderOverview) || features.includes(AppFeatures.OrderMaker) || features.includes(AppFeatures.JwsOverview);
    }
    
    if(location === '/desktop') {
      return features.includes(AppFeatures.OrderOverview) || features.includes(AppFeatures.OrderMaker) || features.includes(AppFeatures.JwsOverview);
    }

    if(isInspectorApp) {
      return features.includes(AppFeatures.Scan);
    }

    if(location === '/') {
      return false;
    }

    return false;
  }

  // const isInspectorApp = pathname.startsWith("/inspector");

  const renderConditions = {
    showStatus: () => showStatus,
    pinModelClosed: () => !isPinModalOpen,
    containsTruckDriverAssignmentToken: () => order.containsTokenType(OrderTokenType.TruckDriverAssignment),
    topLevelTruckDriverAssignmentToken: () => order.hasTopLevelTokenType(OrderTokenType.TruckDriverAssignment),
    notContainsLoadingStartTimeToken: () => !order.containsTokenType(OrderTokenType.LoadingStartTime),
    containsRequestEndLoadingConfigurationToken: () => order.containsTokenType(OrderTokenType.RequestEndLoadingConfirmation),
    notTopLevelTokenLoadingEndTimeConfirmationToken: () => !order.hasTopLevelTokenType(OrderTokenType.LoadingEndTimeConfirmation),
    hasAppFeature: (feature:AppFeatures) => () => featureValidator([feature]),
    isInspectorApp: () => isInspectorApp
  }  

  return (
    <Container maxWidth="sm" className="container" >
      <PinModal
        isOpen={isPinModalOpen}
        onClose={handleClose}
        pin={pin}
        setPin={setPin}
        pinError={pinError}
        handlePinSubmit={submitPin}
        pinAvailable={pinAvailable}
      />
      {/* <DebugTokenButton /> */}
      <Paper elevation={3} className="main-paper" style={{ backgroundColor }}>
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px"  }}>
            <Typography variant="h4" gutterBottom className="app-title">
              {pageTitle}
            </Typography>
            <Account />
          </div>

          <Tabs selectedIndex={selectedTabIndex} onSelect={(index) => setSelectedTabIndex(index)}>
          <TabList>
            <Tab style={{ backgroundColor }}>Digidrop</Tab>
            {(ConditionalRenderer.inlineValidate([
            renderConditions.hasAppFeature(AppFeatures.OrderOverview)])) && (
              <Tab style={{ backgroundColor }}>Orders</Tab>
            )}
            {(ConditionalRenderer.inlineValidate([
            renderConditions.hasAppFeature(AppFeatures.OrderMaker)])) && (
              <Tab style={{ backgroundColor }}>Order Maker</Tab>
            )}
            {(ConditionalRenderer.inlineValidate([
            renderConditions.hasAppFeature(AppFeatures.JwsOverview)])) && (
              <Tab style={{ backgroundColor }}>JWS</Tab>
            )}
          </TabList>

          <TabPanel>

            {(ConditionalRenderer.inlineValidate([
              renderConditions.hasAppFeature(AppFeatures.Scan)])) && (
                <QRScannerBox {...QRScannerBoxProps} featureValidator={featureValidator}/>
            )}
            
            <Card>
            {(ConditionalRenderer.inlineValidate([
              renderConditions.pinModelClosed,
              renderConditions.containsTruckDriverAssignmentToken])) && (
              <CardContent>
                <DriverInformation
                    driver={driverName}
                    truck={truckDetails}
                    purchaseOrderId={purchaseOrderId}
                    description={orderDescription}
                />
              </CardContent>
            )}
            
            {(ConditionalRenderer.inlineValidate([
              renderConditions.showStatus,
              renderConditions.pinModelClosed
            ])) && (
              <CardContent>        
                <Typography variant="h6" gutterBottom>
                  Status
                </Typography>
                <Typography variant="body2" color="textSecondary" className={statusShouldFlash ? "fade-text" : ""}>
                    <strong>{statusText}</strong>
                </Typography>   
              </CardContent>
            )}
            
            {(ConditionalRenderer.inlineValidate([
              renderConditions.topLevelTruckDriverAssignmentToken,
              renderConditions.notContainsLoadingStartTimeToken, 
              renderConditions.pinModelClosed])) && (
              <CardContent className="card-buttons">
                <HandleArrivalOrder handleAcceptOrder={handleAcceptOrder} featureValidator={featureValidator}/>
              </CardContent>
            )}

            {(ConditionalRenderer.inlineValidate([
              renderConditions.containsRequestEndLoadingConfigurationToken,
              renderConditions.pinModelClosed])) && (
              <CardContent>
                <OrderMessages messages={order.messages} driverName={driverName}></OrderMessages>
              </CardContent>
            )}

            {(ConditionalRenderer.inlineValidate([
              renderConditions.isInspectorApp,
              renderConditions.pinModelClosed])) && (
                <Signatures requests={requests} signaturesMode={SignaturesMode.Single} singleUtid={orderId}></Signatures>
            )}
            
            {(ConditionalRenderer.inlineValidate([
              renderConditions.containsRequestEndLoadingConfigurationToken, 
              renderConditions.notTopLevelTokenLoadingEndTimeConfirmationToken,
              renderConditions.pinModelClosed])) && (
              <CardContent className="card-buttons">
                <HandleCompleteOrder handleCompleteOrderWarehouse={handleCompleteOrderWarehouse} featureValidator={featureValidator}/>
              </CardContent>
            )}
            </Card>
          </TabPanel>

          {(ConditionalRenderer.inlineValidate([
          renderConditions.hasAppFeature(AppFeatures.OrderOverview)])) && (
            <TabPanel>
              <OrderOverview onOrderClick={handleOrderClick}></OrderOverview>
            </TabPanel>
          )}

          {(ConditionalRenderer.inlineValidate([
          renderConditions.hasAppFeature(AppFeatures.OrderMaker)])) && (
            <TabPanel>
              <OrderForm  {...orderFormProps}></OrderForm>
            </TabPanel>
          )}

          {(ConditionalRenderer.inlineValidate([
          renderConditions.hasAppFeature(AppFeatures.JwsOverview)])) && (
            <TabPanel>
              <Signatures requests={requests} signaturesMode={SignaturesMode.Selection} singleUtid=""/>
            </TabPanel>
          )}

        </Tabs>
      </Paper>

    </Container>
  );
};
export default App;
